import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Building2, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { apiList } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { useInformacoesClinica } from "@/hooks/useInformacoesClinica";

type ClinicaSaas = {
  id: string;
  cliente_id: string;
  nome: string;
  status?: string;
};

export function ClinicSwitcher() {
  const { user, switchClinic } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [clinicas, setClinicas] = useState<ClinicaSaas[]>([]);
  const [switching, setSwitching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [activeId, setActiveId] = useState(() =>
    typeof window === "undefined" ? "" : localStorage.getItem("odonto.activeClinicaId") || ""
  );
  const { informacoes } = useInformacoesClinica();

  const isSuperAdmin = user?.role === "superadmin" || user?.email === "suporte@cloudservidores.com";
  const canSwitch = !!user && !isSuperAdmin;

  const loadClinics = useCallback(async () => {
    if (!canSwitch) return;
    setLoading(true);
    setLoadFailed(false);
    try {
      const response = await fetch("/api/auth.php?action=clinics", {
        credentials: "include",
        cache: "no-store",
      });
      const payload = await response.json().catch(() => ({}));
      const rows: ClinicaSaas[] = response.ok && Array.isArray(payload?.data)
        ? payload.data
        : await apiList<ClinicaSaas>("clinicas");
        const activeRows = rows.filter((clinica) => (clinica.status || "Ativa") !== "Inativa");
        const localActiveId = localStorage.getItem("odonto.activeClinicaId") || "";
        const preferredId = payload?.active_clinica_id || user?.clinica_id || localActiveId;
        const currentClinica = activeRows.find((clinica) => clinica.id === preferredId)
          || activeRows.find((clinica) => clinica.id === localActiveId);
        setClinicas(activeRows);
        if (currentClinica) {
          const clinicChanged = localActiveId !== currentClinica.id;
          localStorage.setItem("odonto.activeClinicaId", currentClinica.id);
          localStorage.setItem("odonto.activeClinicaName", currentClinica.nome);
          localStorage.setItem("odonto.activeClienteId", currentClinica.cliente_id);
          setActiveId(currentClinica.id);
          if (clinicChanged) window.dispatchEvent(new Event("odonto-clinic-change"));
        } else if (activeRows[0]) {
          const nextClinica = activeRows[0];
          localStorage.setItem("odonto.activeClinicaId", nextClinica.id);
          localStorage.setItem("odonto.activeClinicaName", nextClinica.nome);
          localStorage.setItem("odonto.activeClienteId", nextClinica.cliente_id);
          setActiveId(nextClinica.id);
          window.dispatchEvent(new Event("odonto-clinic-change"));
        }
    } catch {
        setClinicas([]);
        setLoadFailed(true);
        toast({
          title: "Não foi possível carregar as unidades",
          description: "Atualize a página ou entre novamente.",
          variant: "destructive",
        });
    } finally {
      setLoading(false);
    }
  }, [canSwitch, toast, user?.clinica_id]);

  useEffect(() => {
    loadClinics();
  }, [loadClinics, user?.id]);

  const handleClinicChange = async (value: string) => {
    if (!value || value === activeId || switching) return;
    setSwitching(true);
    const { error, clinica } = await switchClinic(value);
    if (error || !clinica) {
      toast({
        title: "Não foi possível trocar de unidade",
        description: error?.message || "Verifique sua permissão para esta clínica.",
        variant: "destructive",
      });
      setSwitching(false);
      return;
    }

    setActiveId(clinica.id);
    await queryClient.cancelQueries();
    queryClient.clear();
    toast({ title: "Unidade alterada", description: clinica.nome });
    window.location.reload();
  };

  const selectedName = useMemo(
    () => clinicas.find((clinica) => clinica.id === activeId)?.nome || "Selecionar clínica",
    [activeId, clinicas]
  );

  if (!canSwitch) return null;

  if (loading && clinicas.length === 0) {
    return (
      <Button variant="outline" className="h-9 w-28 justify-start sm:w-48" disabled>
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        <span className="truncate">Unidades</span>
      </Button>
    );
  }

  if (clinicas.length === 0) {
    return (
      <Button variant="outline" className="h-9 w-28 px-2 sm:w-48" onClick={loadClinics} title="Recarregar unidades">
        <RefreshCw className={`mr-2 h-4 w-4 ${loadFailed ? "text-destructive" : ""}`} />
        <span className="truncate">Recarregar unidades</span>
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-2 min-w-0">
      {switching ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : informacoes?.logo_base64 ? <img src={informacoes.logo_base64} alt="" className="hidden h-7 w-7 rounded-sm object-contain sm:block" /> : <Building2 className="hidden h-4 w-4 text-muted-foreground sm:block" />}
      <Select
        key={activeId}
        value={activeId}
        disabled={switching}
        onValueChange={handleClinicChange}
      >
        <SelectTrigger className="h-9 w-28 bg-background sm:w-48 lg:w-64" aria-label="Selecionar unidade">
          <SelectValue>{selectedName}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {clinicas.map((clinica) => (
            <SelectItem key={clinica.id} value={clinica.id}>
              {clinica.nome}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
